import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { Database } from '@/types/database.generated';
import {
  isMissingAssignedEmailInboxRpc,
  type AssignedPatientEmailInboxRow,
} from '@/lib/dashboardKommunikation';
import {
  unreadUnmatchedEmailCount,
  type UnmatchedEmailThreadRead,
} from '@/lib/unmatchedEmailInbox';

type PatientEmailRealtimeChannel = ReturnType<typeof supabase.channel>;

type SharedPatientEmailRealtime = {
  refs: number;
  channel: PatientEmailRealtimeChannel;
  queryClient: QueryClient;
};

const sharedPatientEmailRealtime = new Map<string, SharedPatientEmailRealtime>();

let focusListenerRefs = 0;
let focusQueryClient: QueryClient | null = null;
let onWindowFocus: (() => void) | null = null;
let onVisibilityChange: (() => void) | null = null;

export function patientEmailRealtimeChannelName(clinicId: string): string {
  return `patient-email:${clinicId}`;
}

function invalidatePatientEmailQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['patient-email-threads'] });
  void queryClient.invalidateQueries({ queryKey: ['patient-email-messages'] });
  void queryClient.invalidateQueries({ queryKey: ['patient-email-thread-reads'] });
}

function acquireFocusListeners(queryClient: QueryClient): void {
  focusQueryClient = queryClient;
  if (focusListenerRefs === 0) {
    onWindowFocus = () => {
      if (focusQueryClient) invalidatePatientEmailQueries(focusQueryClient);
    };
    onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onWindowFocus?.();
    };
    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  focusListenerRefs += 1;
}

function releaseFocusListeners(): void {
  focusListenerRefs = Math.max(0, focusListenerRefs - 1);
  if (focusListenerRefs > 0) return;
  if (onWindowFocus) window.removeEventListener('focus', onWindowFocus);
  if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange);
  onWindowFocus = null;
  onVisibilityChange = null;
  focusQueryClient = null;
}

/**
 * One Realtime channel per clinic. realtime-js returns the existing channel
 * for a duplicate name; calling `.on('postgres_changes')` after `subscribe()`
 * throws and unmounts the app (Sidebar + MobileBottomBar + inbox all call this).
 */
function acquirePatientEmailThreadsRealtime(
  clinicId: string,
  queryClient: QueryClient,
): () => void {
  let shared = sharedPatientEmailRealtime.get(clinicId);
  if (!shared) {
    const invalidate = () => invalidatePatientEmailQueries(queryClient);
    const channel = supabase
      .channel(patientEmailRealtimeChannelName(clinicId))
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patient_email_threads',
          filter: `clinic_id=eq.${clinicId}`,
        },
        invalidate,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patient_email_messages',
          filter: `clinic_id=eq.${clinicId}`,
        },
        invalidate,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patient_email_thread_reads',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: ['patient-email-thread-reads'] });
        },
      )
      .subscribe();
    shared = { refs: 0, channel, queryClient };
    sharedPatientEmailRealtime.set(clinicId, shared);
  } else {
    shared.queryClient = queryClient;
  }
  shared.refs += 1;
  acquireFocusListeners(queryClient);

  return () => {
    const current = sharedPatientEmailRealtime.get(clinicId);
    if (current) {
      current.refs -= 1;
      if (current.refs <= 0) {
        void supabase.removeChannel(current.channel);
        sharedPatientEmailRealtime.delete(clinicId);
      }
    }
    releaseFocusListeners();
  };
}

export function resetPatientEmailThreadsRealtimeForTests(): void {
  for (const entry of sharedPatientEmailRealtime.values()) {
    void supabase.removeChannel(entry.channel);
  }
  sharedPatientEmailRealtime.clear();
  while (focusListenerRefs > 0) releaseFocusListeners();
}

export type PatientEmailThread = Database['public']['Tables']['patient_email_threads']['Row'];
export type PatientEmailMessage = Database['public']['Tables']['patient_email_messages']['Row'];
export type PatientEmailAttachment = Database['public']['Tables']['patient_email_attachments']['Row'];
export type PatientEmailThreadRead = Database['public']['Tables']['patient_email_thread_reads']['Row'];

export const patientEmailThreadsQueryKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['patient-email-threads', clinicId ?? null, patientId ?? null] as const;

export const unmatchedEmailThreadsQueryKey = (clinicId: string | null | undefined) =>
  ['patient-email-threads', 'unmatched', clinicId ?? null] as const;

export const unmatchedEmailReadsQueryKey = (
  clinicId: string | null | undefined,
  userId: string | null | undefined,
) => ['patient-email-thread-reads', clinicId ?? null, userId ?? null] as const;

export const assignedPatientEmailInboxQueryKey = (clinicId: string | null | undefined) =>
  ['patient-email-threads', 'assigned-inbox', clinicId ?? null] as const;

export const patientEmailMessagesQueryKey = (threadId: string | null | undefined) =>
  ['patient-email-messages', threadId ?? null] as const;

export const patientEmailAttachmentsQueryKey = (messageId: string | null | undefined) =>
  ['patient-email-attachments', messageId ?? null] as const;

export function usePatientEmailThreads(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: patientEmailThreadsQueryKey(clinicId, patientId),
    enabled: Boolean(clinicId) && Boolean(patientId),
    queryFn: async (): Promise<PatientEmailThread[]> => {
      if (!clinicId || !patientId) return [];
      const { data, error } = await supabase
        .from('patient_email_threads')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId)
        .neq('inbox_status', 'archived')
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as PatientEmailThread[];
    },
    staleTime: 10_000,
  });
}

export function useUnmatchedEmailThreads(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: unmatchedEmailThreadsQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PatientEmailThread[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('patient_email_threads')
        .select('*')
        .eq('clinic_id', clinicId)
        .is('patient_id', null)
        .eq('inbox_status', 'open')
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as PatientEmailThread[];
    },
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
}

export function useUnmatchedEmailThreadReads(
  clinicId: string | null | undefined,
  userId: string | null | undefined,
) {
  return useQuery({
    queryKey: unmatchedEmailReadsQueryKey(clinicId, userId),
    enabled: Boolean(clinicId) && Boolean(userId),
    queryFn: async (): Promise<UnmatchedEmailThreadRead[]> => {
      if (!clinicId || !userId) return [];
      const { data, error } = await supabase
        .from('patient_email_thread_reads')
        .select('thread_id, last_read_at')
        .eq('clinic_id', clinicId)
        .eq('user_id', userId);
      if (error) throw error;
      return (data ?? []) as UnmatchedEmailThreadRead[];
    },
    staleTime: 8_000,
    refetchOnWindowFocus: true,
  });
}

export type UnmatchedEmailLatestMessage = Pick<
  PatientEmailMessage,
  'thread_id' | 'from_address' | 'text_body' | 'subject' | 'created_at' | 'direction'
>;

export function useUnmatchedEmailLatestMessages(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-email-messages', 'unmatched-latest', clinicId ?? null] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<Map<string, UnmatchedEmailLatestMessage>> => {
      if (!clinicId) return new Map();
      const { data, error } = await supabase
        .from('patient_email_messages')
        .select('thread_id, from_address, text_body, subject, created_at, direction')
        .eq('clinic_id', clinicId)
        .is('patient_id', null)
        .order('created_at', { ascending: false })
        .limit(300);
      if (error) throw error;
      const latest = new Map<string, UnmatchedEmailLatestMessage>();
      for (const row of (data ?? []) as UnmatchedEmailLatestMessage[]) {
        if (!latest.has(row.thread_id)) latest.set(row.thread_id, row);
      }
      return latest;
    },
    staleTime: 8_000,
    refetchOnWindowFocus: true,
  });
}

export type { AssignedPatientEmailInboxRow };

export function useAssignedPatientEmailInbox(clinicId: string | null | undefined) {
  usePatientEmailThreadsRealtime(clinicId ? clinicId : null, 'inbox');
  return useQuery({
    queryKey: assignedPatientEmailInboxQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<AssignedPatientEmailInboxRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase.rpc('list_assigned_patient_email_inbox', {
        p_clinic_id: clinicId,
      });
      if (error) {
        if (isMissingAssignedEmailInboxRpc(error)) return [];
        throw error;
      }
      return (data ?? []) as AssignedPatientEmailInboxRow[];
    },
    staleTime: 8_000,
    refetchOnWindowFocus: true,
  });
}

export function useUnreadUnmatchedEmailCount(
  clinicId: string | null | undefined,
  userId: string | null | undefined,
) {
  usePatientEmailThreadsRealtime(clinicId && userId ? clinicId : null, 'inbox');
  const threads = useUnmatchedEmailThreads(clinicId);
  const reads = useUnmatchedEmailThreadReads(clinicId, userId);
  return {
    ...threads,
    isLoading: threads.isLoading || reads.isLoading,
    data: unreadUnmatchedEmailCount(threads.data ?? [], reads.data ?? []),
    refetch: async () => {
      await Promise.all([threads.refetch(), reads.refetch()]);
    },
  };
}

export function usePatientEmailMessages(threadId: string | null | undefined) {
  return useQuery({
    queryKey: patientEmailMessagesQueryKey(threadId),
    enabled: Boolean(threadId),
    queryFn: async (): Promise<PatientEmailMessage[]> => {
      if (!threadId) return [];
      const { data, error } = await supabase
        .from('patient_email_messages')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as PatientEmailMessage[];
    },
    staleTime: 8_000,
  });
}

export function usePatientEmailInboxMessages(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['patient-email-messages', 'patient', clinicId ?? null, patientId ?? null],
    enabled: Boolean(clinicId) && Boolean(patientId),
    queryFn: async (): Promise<PatientEmailMessage[]> => {
      if (!clinicId || !patientId) return [];
      const { data, error } = await supabase
        .from('patient_email_messages')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PatientEmailMessage[];
    },
    staleTime: 8_000,
  });
}

export function usePatientEmailAttachments(messageId: string | null | undefined) {
  return useQuery({
    queryKey: patientEmailAttachmentsQueryKey(messageId),
    enabled: Boolean(messageId),
    queryFn: async (): Promise<PatientEmailAttachment[]> => {
      if (!messageId) return [];
      const { data, error } = await supabase
        .from('patient_email_attachments')
        .select('*')
        .eq('message_id', messageId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as PatientEmailAttachment[];
    },
    staleTime: 30_000,
  });
}

export function useEmailAttachmentUrl(storagePath: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-email-attachment-url', storagePath ?? null],
    enabled: Boolean(storagePath),
    queryFn: async (): Promise<string | null> => {
      if (!storagePath) return null;
      const { data, error } = await supabase.storage
        .from('patient-email-attachments')
        .createSignedUrl(storagePath, 120);
      if (error) throw error;
      return data.signedUrl;
    },
    staleTime: 60_000,
  });
}

export type SendPatientEmailInput = {
  clinic_id: string;
  patient_id?: string;
  subject: string;
  body: string;
  thread_id?: string;
  attachments?: Array<{ filename: string; content: string; contentType?: string }>;
};

export type SendPatientEmailOutput = {
  thread_id: string;
  message_id: string;
  provider_message_id: string | null;
};

export function useSendPatientEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendPatientEmailInput) =>
      callFunction<SendPatientEmailInput, SendPatientEmailOutput>('send-patient-email', input),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({
        queryKey: patientEmailThreadsQueryKey(input.clinic_id, input.patient_id),
      });
      void qc.invalidateQueries({ queryKey: unmatchedEmailThreadsQueryKey(input.clinic_id) });
      void qc.invalidateQueries({ queryKey: ['email-history', 'patient', input.clinic_id, input.patient_id] });
      void qc.invalidateQueries({
        queryKey: ['patient-email-messages', 'patient', input.clinic_id, input.patient_id],
      });
      if (input.thread_id) {
        void qc.invalidateQueries({ queryKey: patientEmailMessagesQueryKey(input.thread_id) });
      }
    },
  });
}

export function useLinkPatientEmailThread(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { threadId: string; patientId: string }): Promise<PatientEmailThread> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('link_patient_email_thread', {
        p_clinic_id: clinicId,
        p_thread_id: input.threadId,
        p_patient_id: input.patientId,
      });
      if (error) throw error;
      return data as PatientEmailThread;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: unmatchedEmailThreadsQueryKey(clinicId) });
      void qc.invalidateQueries({ queryKey: ['patient-email-thread-reads'] });
      void qc.invalidateQueries({ queryKey: assignedPatientEmailInboxQueryKey(clinicId) });
      void qc.invalidateQueries({
        queryKey: patientEmailThreadsQueryKey(clinicId, row.patient_id),
      });
    },
  });
}

export function useMarkPatientEmailThreadRead(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string): Promise<PatientEmailThreadRead> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('mark_patient_email_thread_read', {
        p_clinic_id: clinicId,
        p_thread_id: threadId,
      });
      if (error) throw error;
      return data as PatientEmailThreadRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['patient-email-thread-reads'] });
      void qc.invalidateQueries({ queryKey: ['patient-email-threads', 'assigned-inbox'] });
    },
  });
}

export function useMarkPatientEmailThreadUnread(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string): Promise<void> => {
      if (!clinicId) throw new Error('No active clinic');
      const { error } = await supabase.rpc('mark_patient_email_thread_unread', {
        p_clinic_id: clinicId,
        p_thread_id: threadId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['patient-email-thread-reads'] });
      void qc.invalidateQueries({ queryKey: ['patient-email-threads', 'assigned-inbox'] });
    },
  });
}

export function useArchivePatientEmailThread(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string): Promise<PatientEmailThread> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('archive_patient_email_thread', {
        p_clinic_id: clinicId,
        p_thread_id: threadId,
      });
      if (error) throw error;
      return data as PatientEmailThread;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: unmatchedEmailThreadsQueryKey(clinicId) });
      void qc.invalidateQueries({
        queryKey: patientEmailThreadsQueryKey(clinicId, row.patient_id),
      });
    },
  });
}

export function usePatientEmailThreadsRealtime(
  clinicId: string | null | undefined,
  _patientId?: string | null,
) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!clinicId) return;
    return acquirePatientEmailThreadsRealtime(clinicId, qc);
  }, [clinicId, qc]);
}

export function useOwnEmailSignature(clinicId: string | null | undefined, userId: string | null | undefined) {
  return useQuery({
    queryKey: ['own-email-signature', clinicId ?? null, userId ?? null],
    enabled: Boolean(clinicId) && Boolean(userId),
    queryFn: async (): Promise<string> => {
      if (!clinicId || !userId) return '';
      const { data, error } = await supabase
        .from('clinic_memberships')
        .select('email_signature_text')
        .eq('clinic_id', clinicId)
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data?.email_signature_text ?? '';
    },
    staleTime: 30_000,
  });
}
